import {
  buildTaskViewUrl,
  createEmptyTaskFilters,
  parseTaskFilters,
  writeTaskFilters,
  type TaskFilters,
} from "./taskFilters";
import type { SavedViewDefinition, SavedViewVisibility, ViewMode } from "./types";

export const SAVED_VIEW_SCHEMA_VERSION = 1;

export type TaskViewDescriptor = {
  baseView: string;
  teamId: string | null;
  filters: TaskFilters;
  search: string;
  layout: ViewMode;
  scopeUserId: string | null;
};

const VALID_BASE_VIEWS = new Set([
  "tasks",
  "inbox",
  "my-issues",
  "awaiting-me",
  "all",
  "active",
  "backlog",
  "board",
]);

function cleanText(value: unknown, max: number): string {
  return typeof value === "string" ? value.trim().slice(0, max) : "";
}

export function createSavedViewDefinition(
  descriptor: TaskViewDescriptor,
  visibility: SavedViewVisibility
): SavedViewDefinition {
  return {
    version: SAVED_VIEW_SCHEMA_VERSION,
    baseView: VALID_BASE_VIEWS.has(descriptor.baseView)
      ? descriptor.baseView
      : "tasks",
    teamId: descriptor.teamId,
    filters: descriptor.filters as unknown as Record<string, unknown>,
    search: descriptor.search.trim().slice(0, 240),
    layout: descriptor.layout,
    // Shared views must remain personal to whoever opens them. Persisting
    // Rafa's UUID would turn "My tasks" into "Rafa's tasks" for everyone.
    scopeUser:
      visibility === "personal"
        ? descriptor.scopeUserId
        : descriptor.scopeUserId
          ? "viewer"
          : null,
  };
}

export function normalizeSavedViewDefinition(
  input: unknown
): SavedViewDefinition | null {
  if (!input || typeof input !== "object" || Array.isArray(input)) return null;
  const value = input as Record<string, unknown>;
  const baseView = cleanText(value.baseView, 40);
  if (!VALID_BASE_VIEWS.has(baseView)) return null;
  const layout = value.layout === "board" ? "board" : value.layout === "list" ? "list" : null;
  if (!layout) return null;

  const teamId =
    value.teamId === null || value.teamId === undefined
      ? null
      : cleanText(value.teamId, 80);
  const scopeUser =
    value.scopeUser === "viewer"
      ? "viewer"
      : value.scopeUser === null || value.scopeUser === undefined
        ? null
        : cleanText(value.scopeUser, 80);

  // Run the JSON through the canonical query parser. Besides providing
  // defaults for future fields, this strips unknown facets and invalid enum
  // values rather than trusting arbitrary saved JSON.
  const rawFilters =
    value.filters && typeof value.filters === "object" && !Array.isArray(value.filters)
      ? (value.filters as Partial<TaskFilters>)
      : {};
  const params = writeTaskFilters({
    ...createEmptyTaskFilters(),
    ...rawFilters,
  } as TaskFilters);
  const filters = parseTaskFilters(params);

  return {
    version: SAVED_VIEW_SCHEMA_VERSION,
    baseView,
    teamId: teamId || null,
    filters: filters as unknown as Record<string, unknown>,
    search: cleanText(value.search, 240),
    layout,
    scopeUser: scopeUser || null,
  };
}

export function savedViewUrl(
  currentUrl: string,
  definition: SavedViewDefinition,
  viewerId: string
): string {
  const normalized = normalizeSavedViewDefinition(definition);
  if (!normalized) throw new Error("invalid_saved_view");
  const scopeUserId =
    normalized.scopeUser === "viewer" ? viewerId : normalized.scopeUser;
  return buildTaskViewUrl(
    currentUrl,
    normalized.filters as unknown as TaskFilters,
    {
      view: normalized.baseView,
      teamId: normalized.teamId,
      search: normalized.search,
      layout: normalized.layout,
      scopeUserId,
      stripEntityDeepLinks: true,
    }
  );
}
